import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';
import { calendarService } from '../services/calendarService';
import { format, addDays, startOfDay, endOfDay, parseISO } from 'date-fns';

const AvailabilityManagement = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSlot, setNewSlot] = useState({
    date: '',
    startTime: '',
    endTime: ''
  });
  const [isGeneratingSlots, setIsGeneratingSlots] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadData();
  }, [user, userProfile, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const startDate = startOfDay(new Date())?.toISOString();
      const endDate = endOfDay(addDays(new Date(), 60))?.toISOString();
      
      const [slotsData, bookingsData] = await Promise.all([
        calendarService?.getAdminAvailabilitySlots(startDate, endDate),
        calendarService?.getAllBookings()
      ]);
      
      setSlots(slotsData || []);
      setBookings(bookingsData || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSlot = async (e) => {
    e?.preventDefault();
    
    try {
      const startDateTime = new Date(`${newSlot?.date}T${newSlot?.startTime}`);
      const endDateTime = new Date(`${newSlot?.date}T${newSlot?.endTime}`);
      
      if (endDateTime <= startDateTime) {
        setError('End time must be after start time');
        return;
      }

      await calendarService?.createAvailabilitySlot({
        startTime: startDateTime?.toISOString(),
        endTime: endDateTime?.toISOString()
      });
      
      setNewSlot({ date: '', startTime: '', endTime: '' });
      setShowCreateForm(false);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to create slot');
    }
  };

  const handleToggleAvailability = async (slotId, currentStatus) => {
    try {
      await calendarService?.updateAvailabilitySlot(slotId, {
        isAvailable: !currentStatus
      });
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update slot');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot?')) {
      return;
    }
    
    try {
      await calendarService?.deleteAvailabilitySlot(slotId);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to delete slot');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await calendarService?.updateBooking(bookingId, { status: newStatus });
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update booking');
    }
  };

  const handleGenerateMondaySlots = async () => {
    try {
      setIsGeneratingSlots(true);
      setError('');
      
      await calendarService?.generateMondaySlots();
      
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to generate Monday slots');
    } finally {
      setIsGeneratingSlots(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'completed': 'bg-blue-100 text-blue-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const groupSlotsByDate = (slots) => {
    const grouped = {};
    slots?.forEach(slot => {
      try {
        const date = format(parseISO(slot?.startTime), 'yyyy-MM-dd');
        if (!grouped?.[date]) {
          grouped[date] = [];
        }
        grouped?.[date]?.push(slot);
      } catch {
        // Skip invalid dates
      }
    });
    return grouped;
  };

  const upcomingBookings = bookings?.filter(b => 
    new Date(b?.scheduledStart) > new Date() && b?.status !== 'cancelled'
  )?.slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading availability management...</p>
          </div>
        </div>
      </div>
    );
  }

  const groupedSlots = groupSlotsByDate(slots);
  const sortedDates = Object.keys(groupedSlots)?.sort();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
      <Helmet>
        <title>Availability Management | Northstar Analytics</title>
      </Helmet>
      <Header />
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Availability Management
            </h1>
            <p className="text-muted-foreground">
              Manage your consultation availability and bookings
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-elevation-2 p-6 mb-8">
            <h2 className="text-xl font-bold text-foreground mb-4">Quick Actions</h2>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={handleGenerateMondaySlots}
                disabled={isGeneratingSlots}
                className="flex items-center space-x-2"
              >
                {isGeneratingSlots ? (
                  <>
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Icon name="CalendarPlus" size={20} />
                    <span>Generate Monday Slots (Next 4 Weeks)</span>
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCreateForm(!showCreateForm)}
              >
                <Icon name="Plus" size={20} className="mr-2" />
                Create Custom Slot
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              Generate Monday slots at 10:00 AM, 11:30 AM, 2:00 PM, and 3:30 PM for the next 4 weeks
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-elevation-1 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={24} className="text-primary" />
                </div>
                <span className="text-3xl font-bold text-foreground">
                  {slots?.filter(s => s?.isAvailable)?.length}
                </span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Available Slots</h3>
            </div>

            <div className="bg-white rounded-xl shadow-elevation-1 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-green-600" />
                </div>
                <span className="text-3xl font-bold text-foreground">
                  {bookings?.filter(b => b?.status === 'confirmed')?.length}
                </span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Confirmed Bookings</h3>
            </div>

            <div className="bg-white rounded-xl shadow-elevation-1 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-yellow-600" />
                </div>
                <span className="text-3xl font-bold text-foreground">
                  {bookings?.filter(b => b?.status === 'pending')?.length}
                </span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Pending Requests</h3>
            </div>
          </div>

          {/* Upcoming Bookings */}
          {upcomingBookings?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-elevation-2 p-8 mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                <Icon name="Calendar" size={28} className="mr-3 text-secondary" />
                Upcoming Bookings
              </h2>
              <div className="space-y-4">
                {upcomingBookings?.map((booking) => (
                  <div
                    key={booking?.id}
                    className="border border-border rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-foreground">{booking?.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking?.status)}`}>
                            {booking?.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground mb-3">
                          <div className="flex items-center">
                            <Icon name="User" size={14} className="mr-2" />
                            {booking?.clientName}
                          </div>
                          <div className="flex items-center">
                            <Icon name="Mail" size={14} className="mr-2" />
                            {booking?.clientEmail}
                          </div>
                          <div className="flex items-center">
                            <Icon name="Calendar" size={14} className="mr-2" />
                            {format(parseISO(booking?.scheduledStart), 'MMM d, yyyy')}
                          </div>
                          <div className="flex items-center">
                            <Icon name="Clock" size={14} className="mr-2" />
                            {format(parseISO(booking?.scheduledStart), 'h:mm a')}
                          </div>
                        </div>
                        {booking?.description && (
                          <p className="text-sm text-muted-foreground">{booking?.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {booking?.status === 'pending' && (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleUpdateBookingStatus(booking?.id, 'confirmed')}
                            >
                              Confirm
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateBookingStatus(booking?.id, 'cancelled')}
                            >
                              Decline
                            </Button>
                          </>
                        )}
                        {booking?.status === 'confirmed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateBookingStatus(booking?.id, 'completed')}
                          >
                            Mark Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Availability Slots */}
          <div className="bg-white rounded-2xl shadow-elevation-2 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground flex items-center">
                <Icon name="Clock" size={28} className="mr-3 text-primary" />
                Availability Slots
              </h2>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowCreateForm(!showCreateForm)}
              >
                Add Slot
              </Button>
            </div>

            {/* Create Slot Form */}
            {showCreateForm && (
              <div className="mb-6 p-6 bg-surface rounded-lg border border-border">
                <h3 className="font-semibold text-foreground mb-4">Create New Slot</h3>
                <form onSubmit={handleCreateSlot} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        required
                        value={newSlot?.date}
                        onChange={(e) => setNewSlot(prev => ({ ...prev, date: e?.target?.value }))}
                        min={format(new Date(), 'yyyy-MM-dd')}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        required
                        value={newSlot?.startTime}
                        onChange={(e) => setNewSlot(prev => ({ ...prev, startTime: e?.target?.value }))}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        End Time
                      </label>
                      <input
                        type="time"
                        required
                        value={newSlot?.endTime}
                        onChange={(e) => setNewSlot(prev => ({ ...prev, endTime: e?.target?.value }))}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <Button type="submit" variant="default">
                      Create Slot
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewSlot({ date: '', startTime: '', endTime: '' });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Slots List */}
            {slots?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="CalendarX" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No availability slots created yet</p>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create Your First Slot
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {sortedDates?.map(date => (
                  <div key={date}>
                    <h3 className="font-semibold text-foreground mb-3">
                      {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                    </h3>
                    <div className="space-y-2">
                      {groupedSlots?.[date]?.map(slot => (
                        <div
                          key={slot?.id}
                          className={`p-4 border rounded-lg flex items-center justify-between ${
                            slot?.isAvailable ? 'border-border bg-white' : 'border-gray-300 bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`w-3 h-3 rounded-full ${
                              slot?.isAvailable ? 'bg-green-500' : 'bg-gray-400'
                            }`} />
                            <div>
                              <p className="font-medium text-foreground">
                                {format(parseISO(slot?.startTime), 'h:mm a')} - {format(parseISO(slot?.endTime), 'h:mm a')}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {slot?.isAvailable ? 'Available' : 'Unavailable'}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleAvailability(slot?.id, slot?.isAvailable)}
                            >
                              {slot?.isAvailable ? 'Mark Unavailable' : 'Mark Available'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Trash2"
                              onClick={() => handleDeleteSlot(slot?.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityManagement;